import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { InvitationError, invitationRequest, type PublicInvitation } from '../api/user-invitations'
import BrandLogo from '../components/BrandLogo'

const providerNames: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  github: 'GitHub',
}
const unavailable: Record<string, string> = {
  expired: 'This invitation has expired. Ask your administrator to send a new invitation.',
  invitation_expired:
    'This invitation has expired. Ask your administrator to send a new invitation.',
  revoked: 'This invitation was revoked. Contact your administrator to request access.',
  invitation_revoked: 'This invitation was revoked. Contact your administrator to request access.',
  consumed: 'This invitation has already been used. Sign in to continue.',
  invitation_used: 'This invitation has already been used. Sign in to continue.',
  disabled: 'Your account is disabled. Contact your administrator.',
}

export default function InvitationSignInPage() {
  const { orgSlug = '', token = '' } = useParams()
  const [params] = useSearchParams()
  const [invitation, setInvitation] = useState<PublicInvitation | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const alert = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    const existing = document.querySelector<HTMLMetaElement>('meta[name="referrer"]')
    const previous = existing?.content
    const meta = existing ?? document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'no-referrer'
    if (!existing) document.head.appendChild(meta)
    return () => {
      if (existing) existing.content = previous ?? ''
      else meta.remove()
    }
  }, [])
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setInvitation(null)
    setError('')
    void invitationRequest<PublicInvitation>(orgSlug, token)
      .then((data) => {
        if (cancelled) return
        setInvitation(data)
        const reason = params.get('error')
        if (reason === 'email_mismatch')
          setError(`Use the account invited at ${data.email}. Your invitation is still valid.`)
        else if (reason)
          setError(
            unavailable[reason] ??
              'Sign-in did not finish. Please try again. Your invitation has not been used.'
          )
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof InvitationError
              ? (unavailable[err.code] ?? err.message)
              : 'Unable to load your invitation. Please try again.'
          )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [orgSlug, token, params])
  useEffect(() => {
    if (error) alert.current?.focus()
  }, [error])
  return (
    <main className="min-h-screen bg-harbor-bg text-harbor-text flex flex-col items-center justify-center px-4 py-10">
      <div className="flex items-center gap-3 mb-8">
        <BrandLogo size={44} />
        <span className="text-xl font-semibold tracking-tight">ContextHarbor</span>
      </div>
      <section className="w-full max-w-md rounded-2xl border border-harbor-border bg-harbor-surface p-6 sm:p-8 shadow-xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
          Your workspace awaits
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          {invitation ? `Join ${invitation.organizationName}` : 'Your invitation'}
        </h1>
        {loading && (
          <p role="status" className="mt-6 text-harbor-text-secondary">
            Checking your invitation…
          </p>
        )}
        {error && (
          <p
            ref={alert}
            tabIndex={-1}
            role="alert"
            className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-harbor-text focus-visible:ring-2 focus-visible:ring-red-500"
          >
            {error}
          </p>
        )}
        {!loading && invitation && (
          <>
            <p className="mt-3 text-sm leading-6 text-harbor-text-secondary">
              Sign in with the account invited at{' '}
              <strong className="font-medium text-harbor-text break-all">{invitation.email}</strong>
              .
            </p>
            <div className="mt-6 space-y-3">
              {invitation.providers.length === 0 && !invitation.hasPassword && (
                <p
                  role="status"
                  className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-harbor-text"
                >
                  Sign-in is not available for this invitation yet. Contact your administrator to
                  enable a sign-in provider or send an invitation with a password.
                </p>
              )}
              {['google', 'microsoft', 'github']
                .filter((provider) => invitation.providers.includes(provider))
                .map((provider) => (
                  <a
                    key={provider}
                    className="flex min-h-12 items-center justify-center rounded-lg border border-harbor-border bg-harbor-bg px-4 font-medium hover:bg-harbor-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    href={`/auth/oauth/${provider}/start?${new URLSearchParams({ invite: token, inviteOrg: orgSlug })}`}
                  >
                    Continue with {providerNames[provider]}
                  </a>
                ))}
            </div>
            {invitation.hasPassword ? (
              <form
                className="mt-6 space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault()
                  const password = String(new FormData(e.currentTarget).get('password'))
                  setBusy(true)
                  setError('')
                  try {
                    const session = await invitationRequest<{
                      accessToken: string
                      refreshToken: string
                      orgSlug: string
                    }>(orgSlug, token, password)
                    window.location.replace(
                      `/${encodeURIComponent(session.orgSlug)}/oauth/complete?${new URLSearchParams({ accessToken: session.accessToken, refreshToken: session.refreshToken })}`
                    )
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Sign-in failed')
                    setBusy(false)
                  }
                }}
              >
                <div className="flex items-center gap-3 text-xs text-harbor-text-secondary">
                  <span className="h-px flex-1 bg-harbor-border" />
                  Use password instead
                  <span className="h-px flex-1 bg-harbor-border" />
                </div>
                <label className="block text-sm">
                  Email
                  <input
                    readOnly
                    value={invitation.email}
                    autoComplete="username"
                    className="mt-1 w-full rounded-lg border border-harbor-border bg-harbor-bg px-3 py-3 text-harbor-text-secondary"
                  />
                </label>
                <label className="block text-sm">
                  Password
                  <input
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className="mt-1 w-full rounded-lg border border-harbor-border bg-harbor-bg px-3 py-3 focus-visible:ring-2 focus-visible:ring-emerald-500"
                  />
                </label>
                <button
                  disabled={busy}
                  className="w-full min-h-12 rounded-lg bg-emerald-600 px-4 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy ? 'Signing in…' : 'Accept invitation and sign in'}
                </button>
              </form>
            ) : (
              <p className="mt-6 text-sm leading-6 text-harbor-text-secondary">
                You can set a password from your profile after signing in.
              </p>
            )}
            <p className="mt-6 border-t border-harbor-border pt-4 text-xs text-harbor-text-secondary">
              Invitation expires {new Date(invitation.expiresAt).toLocaleDateString()}. Opening this
              page does not use your invitation.
            </p>
          </>
        )}
        {!loading && !invitation && (
          <a
            className="mt-6 flex min-h-12 items-center justify-center rounded-lg bg-emerald-600 px-4 font-medium text-white"
            href="/login"
          >
            Go to sign in
          </a>
        )}
      </section>
      <p className="mt-6 text-sm text-harbor-text-secondary">Your team’s knowledge, connected.</p>
    </main>
  )
}
